// src/components/Admin/CompanySettings.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress, Avatar } from '@mui/material';
import { db, storage } from '../../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTenant } from '../../contexts/TenantContext';

const CompanySettings = () => {
  const { tenantId } = useTenant();
  const [companyName, setCompanyName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Beim Laden die aktuellen Daten abrufen
  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!tenantId) return;
      setLoading(true);
      try {
        const tenantDocRef = doc(db, 'tenants', tenantId);
        const tenantSnap = await getDoc(tenantDocRef);
        if (tenantSnap.exists()) {
          const tenantData = tenantSnap.data();
          setCompanyName(tenantData.companyName || '');
          setLogoUrl(tenantData.logoUrl || '');
        } else {
          setError('Tenant-Daten nicht gefunden.');
        }
      } catch (err) {
        console.error(err);
        setError('Fehler beim Abrufen der Unternehmensdaten.');
      } finally {
        setLoading(false);
      }
    };
    fetchCompanySettings();
  }, [tenantId]);

  // Handler für Logo-Datei auswählen
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  // Handler zum Speichern der Einstellungen
  const handleSave = async () => {
    if (!companyName) {
      setError('Bitte den Unternehmensnamen angeben.');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const tenantDocRef = doc(db, 'tenants', tenantId);
      let updatedData = { companyName };

      if (logoFile) {
        // Logo in Firebase Storage hochladen
        const logoRef = ref(storage, `tenants/${tenantId}/logo.${logoFile.name.split('.').pop()}`);
        await uploadBytes(logoRef, logoFile);
        const downloadURL = await getDownloadURL(logoRef);
        updatedData.logoUrl = downloadURL;
      }

      await updateDoc(tenantDocRef, updatedData);
      setMessage('Unternehmensprofil erfolgreich aktualisiert.');
      if (updatedData.logoUrl) {
        setLogoUrl(updatedData.logoUrl);
      }
      setLogoFile(null);
    } catch (err) {
      console.error(err);
      setError('Fehler beim Speichern der Unternehmensdaten.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, margin: '0 auto', p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Unternehmensprofil
      </Typography>

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <Box component="form" noValidate autoComplete="off">
          {/* Unternehmensname */}
          <TextField
            label="Unternehmensname"
            variant="outlined"
            fullWidth
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            sx={{ mb: 3 }}
          />

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={logoUrl}
              alt="Unternehmenslogo"
              sx={{ width: 80, height: 80, mr: 3 }}
            />
            <Button variant="contained" component="label">
              Logo hochladen
              <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
            </Button>
            {logoFile && <Typography variant="body2" sx={{ ml: 2 }}>{logoFile.name}</Typography>}
          </Box>

          {/* Speichern Button */}
          <Button variant="contained" color="primary" onClick={handleSave}>
            Speichern
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CompanySettings;
